import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  $DCprimaryColor,
  colors,
  DcFlexBox,
  DcTag,
  PixelCurrency,
  PixelLeftPanel,
  PixelModal,
  PixelPageContainer,
  PixelRightPanel,
  PixelSpinner,
  PixelText,
  Section,
  SectionContent,
  PixelFactoryContext,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'
import { env } from 'shared/env'
import styled from 'styled-components'

import {
  addNewInvoice,
  getBankAccounts,
  getCustomerPaymentMethods,
  getInvoiceDetail,
  getInvoicesMeta,
} from 'utils/api-routes.util'

import ActionMenu from './components/InvoiceDetailsComponents/ActionMenu'
import Email from './components/InvoiceDetailsComponents/Email'
import LineItems from './components/InvoiceDetailsComponents/LineItems'
import PaymentRecord from './components/InvoiceDetailsComponents/PaymentRecord'
import PayViaStripe from './components/InvoiceDetailsComponents/PayViaStripe'
import ViewPaymentHistory from './components/InvoiceDetailsComponents/ViewPaymentHistory'
import InvoiceInfo from './components/InvoiceInfo'
import { IPixelFactory } from '../../../../decl'
import AddDiscount from '../../orders/OrderDetails/components/AddDiscount'
import Consignment from '../../orders/OrderDetails/components/Consignment'
import CustomerInformation from '../../orders/OrderDetails/components/CustomerInformation'
import OrderShipping from '../../orders/OrderDetails/components/OrderShipping'

interface InvoiceData {
  invoice_payment_status: string
  invoice_status: string
  invoice_sent: string
  invoice_id: string | null
  invoice_payment_received: string
  invoice_balance_amount: string
  source_order_id: string
  customer: {
    customer_id: string
    first_name: string
    last_name: string
    email: string
    phone: string
    company: string
    balance: string
  }
  billing_address: {
    address_id: string
    address1: string
    city: string
    country: string
    country_code: string
    email: string
    first_name: string
    last_name: string
    phone: string
    postcode: string
    zip: string
    state_name: string
    state_code: string
  }
  payments_url: string
  safepay_payments_url: string
  line_items: Array<{
    product_id: number
    total: number
    tax: number
    is_deleted: boolean
  }>
  tempLineItems?: Array<{
    product_id: number
    total: number
    tax: number
    is_deleted: boolean
  }>
  shipping_lines?: Array<{
    total: number
  }>
  discounts?: Array<{
    discount_code: string
    amount: number
  }>
  order_shipping?: Array<{
    total: number
  }>
  discount_lines?: Array<{
    discount_code: string
    amount: number
  }>
}

interface CustomerData {
  customer_id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  company: string
  balance: string
  billing: {
    address_id: string
    address1: string
    city: string
    country: string
    country_code: string
    email: string
    first_name: string
    last_name: string
    phone: string
    postcode: string
    zip: string
    state_name: string
    state_code: string
  }
  shipping: {
    customer_id: string
    first_name: string
    last_name: string
    email: string
    phone: string
    company: string
    balance: string
  }
}

interface InvoiceResponse {
  data: InvoiceData
  _meta: any
}

interface NewInvoiceData {
  line_items: Array<{
    product_id: number
    total: number
    tax: number
    is_deleted: boolean
  }>
  tempLineItems?: Array<{
    product_id: number
    total: number
    tax: number
    is_deleted: boolean
  }>
  shipping_lines?: Array<{
    total: number
  }>
  discounts?: Array<{
    discount_code: string
    amount: number
  }>
}

interface ApiResponse<T> {
  response: T
}

const filterDummy = (d) => d.product_id !== -1

const filterDeleted = (d) => !d.is_deleted

const InvoiceDetails = (props) => {
  const { invoiceId, setInvoiceId } = props

  const navigate = useNavigate()
  const { showFailure, showSuccess, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const chartsRef = React.useRef(null)

  const [options, setOptions] = React.useState<any[]>([])
  const [bank, setBank] = React.useState<any[]>([])
  const [invoiceMeta, setInvoiceMeta] = React.useState<any>({})
  const [customerData, setCustomerData] = React.useState<CustomerData | null>(
    null,
  )
  const [invoiceBalance, setInvoiceBalance] = React.useState<number>(0)
  const [showEmailPopup, setShowEmailPopup] = React.useState(false)
  const [showPaymentModel, setShowPaymentModel] = React.useState(false)
  const [saveLineItems, setSaveLineItems] = React.useState('')
  const [isCustomerAdded, setIsCustomerAdded] = React.useState(false)
  const [PaymentURL, setPaymentURL] = React.useState<string>()
  const [safepayPaymentURL, setSafepayPaymentURL] = React.useState<string>()
  const [isHovered, setIsHovered] = useState(false)
  const [showStripePaymentModel, setShowStripePaymentModel] =
    React.useState(false)
  const [isShowSpinner, setIsShowSpinner] = React.useState(false)

  const [showPaymentHistoryModel, setShowPaymentHistoryModel] =
    React.useState(false)

  const [invoiceData, setInvoiceData] = React.useState<InvoiceData | null>(null)
  const [newInvoiceData, setNewInvoiceData] = React.useState<NewInvoiceData>({
    line_items: [],
  })

  const fetchInvoiceDetail = () => {
    setIsShowSpinner(true)
    getInvoiceDetail(invoiceId).subscribe((res: AjaxResponse<any>) => {
      setIsShowSpinner(false)
      const response = res.response as InvoiceResponse
      const data = response?.data
      if (data) {
        setInvoiceData(data)

        if (data.customer) {
          setCustomerData({
            ...data.customer,
            billing: data.billing_address,
            shipping: data.customer,
          })
        }
        if (!PaymentURL) {
          setPaymentURL(data.payments_url)
        }
        if (!safepayPaymentURL) {
          setSafepayPaymentURL(data.safepay_payments_url)
        }
        if (response?._meta) {
          setInvoiceMeta(response._meta)
        }
      }
    })
  }
  const fetchInvoiceMeta = () => {
    getInvoicesMeta().subscribe(() => {
      setNewInvoiceData((prev) => ({
        ...prev,
      }))
    })
  }

  const baseURL = env.SERVER_ADDR
  const handleAddNewInvoice = (action) => {
    showPageLoader(chartsRef)
    const data = { ...newInvoiceData }
    data.line_items = data.line_items.filter(filterDeleted).filter(filterDummy)
    addNewInvoice(data).subscribe((res: AjaxResponse<any>) => {
      hidePageLoader()
      const response = res.response as { invoice_id: string; field?: string }
      if (response?.invoice_id) {
        if (action === 'print') {
          const { token } = JSON.parse(
            localStorage.getItem('user_info') || '{token: null}',
          )
          const link = `${baseURL}api/v2/print/invoice/${token}/${response.invoice_id}`
          window.open(link, '_blank')
          navigate(`/invoice/detail/${response.invoice_id}`)
        } else if (action === 'newInvoice') {
          showSuccess('Added successfully')
          navigate(`/invoice/detail/${response.invoice_id}`)
        }
      } else {
        showFailure(`${response.field} is required`)
      }
    })
  }

  const getData = () => {
    return invoiceId === 'new'
      ? [
          ...((newInvoiceData?.tempLineItems
            ? newInvoiceData.tempLineItems
            : newInvoiceData.line_items
          )
            ?.filter(filterDeleted)
            .filter(filterDummy) || []),
        ]
      : [
          ...((invoiceData?.tempLineItems
            ? invoiceData.tempLineItems
            : invoiceData?.line_items
          )
            ?.filter(filterDeleted)
            .filter(filterDummy) || []),
        ]
  }

  const getNewInvoiceTotal = () => {
    const grandTotal = [...getData()].reduce((acc, item) => {
      const total = item.is_deleted ? 0 : Number(item.total)
      return Number(acc) + total
    }, 0)
    const shipping = newInvoiceData.shipping_lines?.reduce((acc, cur) => {
      return acc + Number(cur.total)
    }, 0)
    const fixedDiscount = newInvoiceData.discounts?.reduce((acc, cur) => {
      return cur.discount_code === 'fixed_amount'
        ? acc + Number(cur.amount)
        : acc
    }, 0)
    const percentageDiscount =
      newInvoiceData.discounts?.reduce((acc, cur) => {
        return cur.discount_code === 'percentage'
          ? acc + Number(cur.amount)
          : acc
      }, 0) ?? 0
    let pdiscount = 0
    if (percentageDiscount <= 100) {
      pdiscount = (grandTotal * percentageDiscount) / 100
    }

    return (
      (grandTotal || 0) +
      (shipping || 0) -
      (fixedDiscount || 0) -
      (pdiscount || 0) +
      (getInvoiceTotalTax() || 0)
    )
  }
  const getExistingInvoiceTotal = () => {
    if (!invoiceData) return 0

    const grandTotal = [...getData()].reduce((acc, item) => {
      const total = item.is_deleted ? 0 : Number(item.total)
      return Number(acc) + total
    }, 0)
    const shipping = invoiceData.order_shipping?.reduce((acc, cur) => {
      return acc + Number(cur.total)
    }, 0)
    const fixedDiscount = invoiceData.discount_lines?.reduce((acc, cur) => {
      return cur.discount_code === 'fixed_amount'
        ? acc + Number(cur.amount)
        : acc
    }, 0)

    const percentageDiscount =
      invoiceData.discount_lines?.reduce((acc, cur) => {
        return cur.discount_code === 'percentage'
          ? acc + Number(cur.amount)
          : acc
      }, 0) ?? 0
    let pdiscount = 0
    if (percentageDiscount <= 100) {
      pdiscount = (grandTotal * percentageDiscount) / 100
    }
    return (
      (grandTotal || 0) +
      (shipping || 0) -
      (fixedDiscount || 0) -
      (pdiscount || 0) +
      (getInvoiceTotalTax() || 0)
    )
  }
  const getInvoiceTotalTax = () => {
    return [...getData()].reduce((acc, item) => {
      const tax = item.is_deleted ? 0 : Number(item.tax)
      return Number(acc) + tax
    }, 0)
  }

  const getPaymentURL = (URL) => {
    setPaymentURL(URL)
  }

  const getPaymentSafepayURL = (URL) => {
    setSafepayPaymentURL(URL)
  }

  const getInvoiceTotal = () => {
    return invoiceId === 'new'
      ? getNewInvoiceTotal()
      : getExistingInvoiceTotal()
  }

  const getMethods = () => {
    getCustomerPaymentMethods().subscribe((res: AjaxResponse<any>) => {
      const response = res.response as { data: any[] }
      setOptions(response?.data || [])
    })
  }

  const getBank = () => {
    getBankAccounts().subscribe((res: AjaxResponse<any>) => {
      const response = res.response as { data: any[] }
      setBank(response?.data || [])
    })
  }

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  React.useEffect(() => {
    setInvoiceBalance(invoiceData?.invoice_balance_amount)
  }, [invoiceData])

  React.useEffect(() => {
    getMethods()
    getBank()
  }, [])

  React.useEffect(() => {
    if (invoiceId === 'new') {
      setInvoiceData(null)
      setCustomerData(null)
    } else {
      fetchInvoiceDetail()
      fetchInvoiceMeta()
    }
  }, [invoiceId])

  return (
    <>
      <PixelSpinner isShow={isShowSpinner} />

      <PixelPageContainer style={{ padding: '0px' }} ref={chartsRef}>
        <DcFlexBox
          flexDirection="column"
          justifyContent="center"
          width="100%"
          margin="0 auto"
          height="auto"
          style={{
            transition: ' all 0.3s ease-in-out',
          }}
        >
          <DcFlexBox
            style={{ backgroundColor: colors.white }}
            padding="0.5rem 0.5rem"
            width="100%"
            height="auto"
          >
            <PixelLeftPanel justifyContent="flex-start" width={'40%'}>
              <DcFlexBox>
                <PixelText
                  textSize="16px"
                  variant="dark"
                  style={{ fontWeight: '600', paddingRight: '5px' }}
                >
                  Invoice # {invoiceData?.source_order_id}
                </PixelText>

                <DcTag
                  textColor={
                    invoiceData?.invoice_payment_status == 'paid'
                      ? colors.warning
                      : colors.danger
                  }
                  customBackgroundColor={
                    invoiceData?.invoice_payment_status == 'paid'
                      ? colors.warningLight
                      : colors.dangerLight
                  }
                >
                  {invoiceData?.invoice_payment_status}
                </DcTag>
              </DcFlexBox>
            </PixelLeftPanel>
            <PixelRightPanel
              justifyContent="flex-end"
              alignItems="flex-end"
              gap="10px"
            >
              <ActionMenu
                invoiceId={invoiceId}
                newInvoiceData={newInvoiceData}
                invoiceData={invoiceData}
                invoiceMeta={invoiceMeta}
                setInvoiceData={setInvoiceData}
                setSaveLineItems={setSaveLineItems}
                setShowEmailPopup={setShowEmailPopup}
                setShowPaymentModel={setShowPaymentModel}
                setShowStripePaymentModel={setShowStripePaymentModel}
                setShowPaymentHistoryModel={setShowPaymentHistoryModel}
                handleAddNewInvoice={handleAddNewInvoice}
                isCustomerAdded={isCustomerAdded}
                customerId={customerData.customer_id}
                getPaymentURL={getPaymentURL}
                getPaymentSafepayURL={getPaymentSafepayURL}
                setInvoiceId={setInvoiceId}
              />
            </PixelRightPanel>
          </DcFlexBox>
          <DcFlexBox
            padding="20px 12px 20px 12px"
            width="100%"
            height="auto"
            overflow="auto"
          >
            <PixelLeftPanel
              flexDirection="column"
              justifyContent="flex-start"
              width="65%"
              gap="18px"
            >
              <Section noPadding>
                <SectionContent noPadding>
                  <LineItems
                    detailID={invoiceData?.invoice_id}
                    saveLineItems={saveLineItems}
                    setSaveLineItems={setSaveLineItems}
                    setDetails={
                      invoiceId === 'new' ? setNewInvoiceData : setInvoiceData
                    }
                    details={invoiceId === 'new' ? newInvoiceData : invoiceData}
                    newInvoice={invoiceId === 'new'}
                  />
                </SectionContent>
              </Section>

              <Section noPadding>
                <SectionContent noPadding>
                  <AddDiscount
                    detailID={invoiceData?.invoice_id}
                    setDetails={setInvoiceData}
                    details={invoiceData}
                    newOrder={invoiceId === 'new'}
                    isItInvoice={true}
                    newInvoice={invoiceId === 'new'}
                    newOrderData={newInvoiceData}
                    setNewOrderData={setNewInvoiceData}
                  />
                </SectionContent>
              </Section>

              <Section noPadding>
                <SectionContent noPadding>
                  <OrderShipping
                    detailID={invoiceData?.invoice_id}
                    setDetails={setInvoiceData}
                    details={invoiceData}
                    isItInvoice={true}
                    newOrder={invoiceId === 'new'}
                    newOrderData={newInvoiceData}
                    setNewOrderData={setNewInvoiceData}
                    invoiceNew={invoiceId === 'new'}
                    invoiceStatus={invoiceData.invoice_status}
                  />
                </SectionContent>
              </Section>

              <Section noPadding>
                <SectionContent noPadding>
                  <InnerSection>
                    <PixelText
                      style={{
                        fontWeight: '600',
                        color: colors.grayTextDark,
                      }}
                    >
                      Invoice Summary
                    </PixelText>
                  </InnerSection>
                  <InnerSection>
                    <DcFlexBox alignItems={'flex-end'} flexDirection={'column'}>
                      <PixelText style={{ fontSize: '12px' }}>
                        Tax:
                        <strong>
                          <PixelCurrency value={getInvoiceTotalTax()} />
                        </strong>
                      </PixelText>
                      <PixelText style={{ fontSize: '12px' }}>
                        Total:
                        <strong>
                          <PixelCurrency value={getInvoiceTotal()} />
                        </strong>
                      </PixelText>
                      <PixelText style={{ fontSize: '12px' }}>
                        Amount Paid:
                        <strong>
                          <PixelCurrency
                            value={invoiceData.invoice_payment_received || 0.0}
                          />
                        </strong>
                      </PixelText>
                      <PixelText style={{ fontSize: '12px' }}>
                        Balance Due:
                        <strong>
                          <PixelCurrency value={invoiceBalance || 0.0} />
                        </strong>
                      </PixelText>
                    </DcFlexBox>
                  </InnerSection>
                </SectionContent>
              </Section>
              {PaymentURL && (
                <Section noPadding>
                  <SectionContent noPadding>
                    <InnerSection
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '5px , 10px, 5px,10px',
                        flexDirection: 'row',
                      }}
                    >
                      <PixelText
                        style={{
                          fontWeight: '600',
                          color: colors.grayTextDark,
                        }}
                      >
                        Stripe Payment Link
                      </PixelText>
                      <PixelText
                        style={{
                          fontWeight: '600',
                          color: colors.grayTextDark,
                        }}
                      >
                        <a
                          style={{
                            textDecoration: isHovered ? 'underline' : 'none',
                            color: $DCprimaryColor,
                          }}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                          href={PaymentURL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Pay Invoice
                        </a>
                      </PixelText>
                    </InnerSection>
                  </SectionContent>
                </Section>
              )}

              {safepayPaymentURL && (
                <Section noPadding>
                  <SectionContent noPadding>
                    <InnerSection
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '5px , 10px, 5px,10px',
                        flexDirection: 'row',
                      }}
                    >
                      <PixelText
                        style={{
                          fontWeight: '600',
                          color: colors.grayTextDark,
                        }}
                      >
                        Safepay Payment Link
                      </PixelText>
                      <PixelText
                        style={{
                          fontWeight: '600',
                          color: colors.grayTextDark,
                        }}
                      >
                        <a
                          style={{
                            textDecoration: isHovered ? 'underline' : 'none',
                            color: $DCprimaryColor,
                          }}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                          href={safepayPaymentURL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Pay Invoice
                        </a>
                      </PixelText>
                    </InnerSection>
                  </SectionContent>
                </Section>
              )}
            </PixelLeftPanel>
            <PixelRightPanel
              flexDirection="column"
              justifyContent="flex-start"
              width="35%"
              gap="18px"
            >
              <Section noPadding>
                <SectionContent noPadding>
                  <InvoiceInfo
                    detailID={invoiceData?.invoice_id}
                    setDetails={setInvoiceData}
                    details={invoiceData}
                    newOrder={invoiceId === 'new'}
                    newOrderData={newInvoiceData}
                    setNewOrderData={setNewInvoiceData}
                  />
                </SectionContent>
              </Section>
              <Section noPadding>
                <SectionContent noPadding>
                  <CustomerInformation
                    detailID={invoiceData?.invoice_id}
                    setDetails={
                      invoiceId === 'new' ? setNewInvoiceData : setInvoiceData
                    }
                    details={invoiceId === 'new' ? newInvoiceData : invoiceData}
                    newOrder={invoiceId === 'new'}
                    invoiceNew={invoiceId === 'new'}
                    isItInvoice={true}
                    newOrderData={newInvoiceData}
                    setNewOrderData={setNewInvoiceData}
                    customerData={customerData}
                    setCustomerData={setCustomerData}
                    setIsCustomerAdded={setIsCustomerAdded}
                  />
                </SectionContent>
              </Section>
              {invoiceId !== 'new' && (
                <Section noPadding>
                  <SectionContent noPadding>
                    <Consignment
                      getOrderDetails={fetchInvoiceDetail}
                      detailID={invoiceId}
                      setDetails={setInvoiceData}
                      order={invoiceData}
                    />
                  </SectionContent>
                </Section>
              )}
            </PixelRightPanel>
          </DcFlexBox>
        </DcFlexBox>
      </PixelPageContainer>
      {showEmailPopup && (
        <PixelModal
          title={'Send Email'}
          handleClose={() => setShowEmailPopup(false)}
          show={showEmailPopup}
        >
          {showEmailPopup && (
            <Email
              customerData={customerData}
              invoiceData={invoiceData}
              invoiceId={invoiceId}
              handleClose={() => setShowEmailPopup(false)}
            />
          )}
        </PixelModal>
      )}
      {showPaymentModel && (
        <PixelModal
          title={'Record Payment'}
          handleClose={() => setShowPaymentModel(false)}
          show={showPaymentModel}
          size="md"
        >
          {showPaymentModel && (
            <PaymentRecord
              customerData={customerData}
              invoiceData={invoiceData}
              invoiceId={invoiceId}
              options={options}
              bank={bank}
              handleClose={(data: IUpdateInvoiceStatus) => {
                if (data) {
                  setInvoiceData(() => ({
                    ...invoiceData,
                    invoice_payment_status: data?.invoice_payment_status,
                    invoice_status: data?.invoice_status,
                    invoice_payment_received: data?.invoice_payment_received,
                    invoice_balance_amount: data?.invoice_balance_amount,
                  }))
                }
                setShowPaymentModel(false)
              }}
            />
          )}
        </PixelModal>
      )}
      {showStripePaymentModel && (
        <PixelModal
          title={'Pay Via Stripe'}
          handleClose={() => setShowStripePaymentModel(false)}
          show={showStripePaymentModel}
          size="md"
        >
          {showStripePaymentModel && (
            <PayViaStripe
              customerData={customerData}
              invoiceData={invoiceData}
              invoiceId={invoiceId}
              handleClose={(data: IUpdateInvoiceStatus) => {
                if (data) {
                  setInvoiceData(() => ({
                    ...invoiceData,
                    invoice_payment_status: data?.invoice_payment_status,
                    invoice_status: data?.invoice_status,
                    invoice_payment_received: data?.invoice_payment_received,
                    invoice_balance_amount: data?.invoice_balance_amount,
                  }))
                }
                setShowStripePaymentModel(false)
              }}
            />
          )}
        </PixelModal>
      )}

      {showPaymentHistoryModel && (
        <PixelModal
          title={'Customer Payment History'}
          handleClose={() => setShowPaymentHistoryModel(false)}
          show={showPaymentHistoryModel}
          size={{
            minWidth: '60vw',
            maxheight: '90vh',
          }}
        >
          {showPaymentHistoryModel && (
            <ViewPaymentHistory
              invoice_id={invoiceData.invoice_id}
              handleClose={() => {
                setShowPaymentHistoryModel(false)
              }}
            />
          )}
        </PixelModal>
      )}
    </>
  )
}

const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-content: center;
  border-bottom: 1px solid ${colors.grayBorderLight};
  width: 100%;
  padding: 10px;
`
export default InvoiceDetails
